import React from 'react';
import { Code2, Terminal, Coffee, CheckCircle2, Sun, Moon } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const DocsPage = () => {
  const { theme, toggleTheme } = useTheme();

  const themeStyles = {
    dark: {
      background: 'bg-[#0d0d0d]',
      text: 'text-white',
      card: 'bg-black/30 border-white/10',
      codeBlock: 'bg-black/60',
      textSecondary: 'text-gray-400',
      textMuted: 'text-gray-300',
    },
    light: {
      background: 'bg-gray-50',
      text: 'text-gray-900',
      card: 'bg-white border-gray-200',
      codeBlock: 'bg-gray-900',
      textSecondary: 'text-gray-600',
      textMuted: 'text-gray-700',
    }
  };

  const styles = themeStyles[theme];

  return (
    <div className={`min-h-screen ${styles.background} ${styles.text}`}>
      {/* Header */}
      <header className={`border-b ${theme === 'dark' ? 'border-white/10 bg-black/30' : 'border-gray-200 bg-white'} backdrop-blur-sm sticky top-0 z-20`}>
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Terminal className="w-8 h-8 text-[#f2055c]" />
              <h1 className="text-2xl font-bold">Setup Guide</h1>
            </div>
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg ${theme === 'dark' ? 'hover:bg-white/10' : 'hover:bg-gray-100'} transition-colors`}
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? (
                <Sun className="w-5 h-5 text-[#f2055c]" />
              ) : (
                <Moon className="w-5 h-5 text-[#f2055c]" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        {/* Headline Section */}
        <div className="mb-12 text-center">
          <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-[#f2055c] to-[#f20587] text-transparent bg-clip-text">
            Guia de Instalação e Configuração
          </h2>
          <p className={`text-xl ${styles.textSecondary} max-w-2xl mx-auto`}>
            Configure seu ambiente de desenvolvimento Java e VSCode de forma rápida e eficiente.
          </p>
        </div>

        {/* Content Sections */}
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Java Section */}
          <div className={`${styles.card} border rounded-lg overflow-hidden`}>
            <div className="p-6 space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#f2055c]/10 flex items-center justify-center">
                  <Coffee className="w-6 h-6 text-[#f2055c]" />
                </div>
                <h3 className="text-2xl font-bold">Java 17</h3>
              </div>

              {/* Installation Steps Infographic */}
              <div className="py-6">
                <div className="flex flex-col md:flex-row gap-4 justify-between items-stretch">
                  <div className={`flex-1 p-4 rounded-lg ${theme === 'dark' ? 'bg-black/20' : 'bg-gray-50'} border ${theme === 'dark' ? 'border-white/10' : 'border-gray-200'}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-full bg-[#f2055c]/10 flex items-center justify-center text-[#f2055c] font-bold">1</div>
                      <h4 className="font-semibold">Download</h4>
                    </div>
                    <p className={`${styles.textMuted} text-sm mb-3`}>Baixe o JDK 17 do site oficial da Oracle</p>
                    <div className={`text-xs ${styles.textSecondary} flex items-center gap-1`}>
                      <span className="block w-2 h-2 rounded-full bg-[#f2055c]"></span>
                      Escolha a versão para seu SO
                    </div>
                  </div>
                  
                  <div className="hidden md:flex items-center text-[#f2055c]">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>

                  <div className={`flex-1 p-4 rounded-lg ${theme === 'dark' ? 'bg-black/20' : 'bg-gray-50'} border ${theme === 'dark' ? 'border-white/10' : 'border-gray-200'}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-full bg-[#f2055c]/10 flex items-center justify-center text-[#f2055c] font-bold">2</div>
                      <h4 className="font-semibold">Instalação</h4>
                    </div>
                    <p className={`${styles.textMuted} text-sm mb-3`}>Execute o instalador e anote o diretório</p>
                    <div className={`text-xs ${styles.textSecondary} flex items-center gap-1`}>
                      <span className="block w-2 h-2 rounded-full bg-[#f2055c]"></span>
                      Mantenha as opções padrão
                    </div>
                  </div>

                  <div className="hidden md:flex items-center text-[#f2055c]">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>

                  <div className={`flex-1 p-4 rounded-lg ${theme === 'dark' ? 'bg-black/20' : 'bg-gray-50'} border ${theme === 'dark' ? 'border-white/10' : 'border-gray-200'}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-full bg-[#f2055c]/10 flex items-center justify-center text-[#f2055c] font-bold">3</div>
                      <h4 className="font-semibold">Configuração</h4>
                    </div>
                    <p className={`${styles.textMuted} text-sm mb-3`}>Configure as variáveis de ambiente</p>
                    <div className={`text-xs ${styles.textSecondary} flex items-center gap-1`}>
                      <span className="block w-2 h-2 rounded-full bg-[#f2055c]"></span>
                      JAVA_HOME e PATH
                    </div>
                  </div>
                </div>
              </div>

              {/* Windows */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-lg font-semibold">
                  <span className="text-[#f2055c]">01.</span>
                  Windows
                </div>
                <div className="pl-6 space-y-4">
                  <div>
                    <h4 className="text-[#f2055c] mb-2 font-medium">Download do JDK 17</h4>
                    <ul className={`list-disc pl-5 ${styles.textMuted} space-y-1`}>
                      <li>Acesse <a href="https://www.oracle.com/java/technologies/downloads/#java17" className="text-[#f2055c] hover:text-[#ff1a75] transition-colors duration-200" target="_blank" rel="noopener noreferrer">Oracle JDK 17 Downloads</a></li>
                      <li>Baixe o instalador Windows (x64) .msi</li>
                      <li>Execute o instalador e <strong>anote o diretório de instalação</strong> - você precisará dele no próximo passo</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-[#f2055c] mb-2 font-medium">Configurar Variáveis de Ambiente</h4>
                    <div className="mb-4">
                      <p className={`${styles.textMuted} mb-2`}><strong>⚠️ Importante:</strong> Substitua o caminho abaixo pelo diretório onde o Java foi instalado no seu computador.</p>
                      <p className={`${styles.textMuted} mb-2`}>O caminho padrão costuma ser <code className="bg-black/20 px-2 py-1 rounded">C:\Program Files\Java\jdk-17</code>, mas pode variar dependendo:</p>
                      <ul className={`list-disc pl-5 ${styles.textMuted} space-y-1 mb-4`}>
                        <li>Da versão exata do JDK instalado</li>
                        <li>Do diretório escolhido durante a instalação</li>
                        <li>Se você tem outras versões do Java instaladas</li>
                      </ul>
                    </div>
                    <div className={`${styles.codeBlock} rounded-lg p-4 font-mono text-sm border ${theme === 'dark' ? 'border-white/10' : 'border-gray-700'}`}>
                      {/* Windows PowerShell commands */}
                      <div className="flex justify-between items-center mb-2">
                        <span className={`text-xs ${styles.textSecondary}`}>PowerShell</span>
                      </div>
                      <code className="text-gray-300 whitespace-pre">
{`# Abra o PowerShell como Administrador e execute:
# IMPORTANTE: Substitua o caminho abaixo pelo diretório correto da sua instalação
setx JAVA_HOME "C:\\Program Files\\Java\\jdk-17"
setx PATH "%PATH%;%JAVA_HOME%\\bin"`}</code>
                    </div>
                  </div>
                </div>
              </div>

              {/* macOS */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-lg font-semibold">
                  <span className="text-[#f2055c]">02.</span>
                  macOS
                </div>
                <div className="pl-6 space-y-4">
                  <div>
                    <h4 className="text-[#f2055c] mb-2 font-medium">Usando Homebrew</h4>
                    <div className="mb-4">
                      <p className={`${styles.textMuted} mb-2`}><strong>📝 Nota:</strong> Após a instalação, o Homebrew mostrará instruções específicas para configurar o Java no seu sistema. Copie e execute os comandos mostrados.</p>
                    </div>
                    <div className={`${styles.codeBlock} rounded-lg p-4 font-mono text-sm border ${theme === 'dark' ? 'border-white/10' : 'border-gray-700'}`}>
                      {/* macOS Homebrew commands */}
                      <div className="flex justify-between items-center mb-2">
                        <span className={`text-xs ${styles.textSecondary}`}>Terminal</span>
                      </div>
                      <code className="text-gray-300 whitespace-pre">
{`# Instalar Homebrew (se não tiver)
/bin/bash -c "$(curl -fsSL https://raw.githubusercontent.com/Homebrew/install/HEAD/install.sh)"

# Instalar OpenJDK 17
brew install openjdk@17`}</code>
                    </div>
                  </div>
                </div>
              </div>

              {/* Linux */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-lg font-semibold">
                  <span className="text-[#f2055c]">03.</span>
                  Linux (Ubuntu/Debian)
                </div>
                <div className="pl-6 space-y-4">
                  <div>
                    <h4 className="text-[#f2055c] mb-2 font-medium">Instalação e Configuração</h4>
                    <div className="mb-4">
                      <p className={`${styles.textMuted} mb-2`}><strong>⚠️ Importante:</strong> O caminho do JAVA_HOME pode variar dependendo da sua distribuição Linux e da versão exata do OpenJDK instalada.</p>
                      <p className={`${styles.textMuted} mb-2`}>Para encontrar o caminho correto, após a instalação execute:</p>
                      <div className={`${styles.codeBlock} rounded-lg p-4 font-mono text-sm border ${theme === 'dark' ? 'border-white/10' : 'border-gray-700'} mb-4`}>
                        <code className="text-gray-300 whitespace-pre">
{`sudo update-alternatives --config java`}</code>
                      </div>
                      <p className={`${styles.textMuted} mb-2`}>Use o caminho mostrado (removendo o /bin/java do final) nas configurações abaixo.</p>
                    </div>
                    <div className={`${styles.codeBlock} rounded-lg p-4 font-mono text-sm border ${theme === 'dark' ? 'border-white/10' : 'border-gray-700'}`}>
                      {/* Linux commands */}
                      <div className="flex justify-between items-center mb-2">
                        <span className={`text-xs ${styles.textSecondary}`}>bash</span>
                      </div>
                      <code className="text-gray-300 whitespace-pre">
{`# Atualizar e instalar
sudo apt update && sudo apt upgrade
sudo apt install openjdk-17-jdk

# Configurar variáveis
echo 'export JAVA_HOME=/usr/lib/jvm/java-17-openjdk-amd64' >> ~/.bashrc
echo 'export PATH=$JAVA_HOME/bin:$PATH' >> ~/.bashrc
source ~/.bashrc`}</code>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* VSCode Section */}
          <div className={`${styles.card} border rounded-lg overflow-hidden`}>
            <div className="p-6 space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#f2055c]/10 flex items-center justify-center">
                  <Code2 className="w-6 h-6 text-[#f2055c]" />
                </div>
                <h3 className="text-2xl font-bold">VSCode Setup</h3>
              </div>

              {/* VSCode Setup Infographic */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-black/20' : 'bg-gray-50'} border ${theme === 'dark' ? 'border-white/10' : 'border-gray-200'}`}>
                  <div className="w-12 h-12 rounded-lg bg-[#f2055c]/10 flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-[#f2055c]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  </div>
                  <h4 className="font-semibold mb-2">Instalação Básica</h4>
                  <p className={`${styles.textMuted} text-sm`}>Download e instalação do VSCode para seu sistema operacional</p>
                </div>

                <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-black/20' : 'bg-gray-50'} border ${theme === 'dark' ? 'border-white/10' : 'border-gray-200'}`}>
                  <div className="w-12 h-12 rounded-lg bg-[#f2055c]/10 flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-[#f2055c]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <h4 className="font-semibold mb-2">Extensões Java</h4>
                  <p className={`${styles.textMuted} text-sm`}>Instalação do pacote de extensões essenciais para desenvolvimento Java</p>
                </div>

                <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-black/20' : 'bg-gray-50'} border ${theme === 'dark' ? 'border-white/10' : 'border-gray-200'}`}>
                  <div className="w-12 h-12 rounded-lg bg-[#f2055c]/10 flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-[#f2055c]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h4 className="font-semibold mb-2">Spring Boot</h4>
                  <p className={`${styles.textMuted} text-sm`}>Extensões específicas para desenvolvimento com Spring Boot</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-2 text-lg font-semibold">
                  <span className="text-[#f2055c]">01.</span>
                  Extensões Essenciais
                </div>
                <div className="pl-6">
                  <div className={`${styles.codeBlock} rounded-lg border ${theme === 'dark' ? 'border-white/10' : 'border-gray-700'} overflow-hidden`}>
                    <table className="w-full">
                      <thead className={`border-b ${theme === 'dark' ? 'border-white/10' : 'border-gray-700'}`}>
                        <tr>
                          <th className={`py-3 px-4 text-left ${styles.textSecondary} font-mono`}>EXTENSION</th>
                          <th className={`py-3 px-4 text-left ${styles.textSecondary} font-mono`}>ID</th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${theme === 'dark' ? 'divide-white/10' : 'divide-gray-200'}`}>
                        <tr>
                          <td className={`py-3 px-4 ${styles.textMuted}`}>Extension Pack for Java</td>
                          <td className="py-3 px-4 font-mono text-sm text-[#f2055c]">vscjava.vscode-java-pack</td>
                        </tr>
                        <tr>
                          <td className={`py-3 px-4 ${styles.textMuted}`}>Language Support for Java</td>
                          <td className="py-3 px-4 font-mono text-sm text-[#f2055c]">redhat.java</td>
                        </tr>
                        <tr>
                          <td className={`py-3 px-4 ${styles.textMuted}`}>Maven for Java</td>
                          <td className="py-3 px-4 font-mono text-sm text-[#f2055c]">vscjava.vscode-maven</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-2 text-lg font-semibold">
                  <span className="text-[#f2055c]">02.</span>
                  Extensões Spring Boot
                </div>
                <div className="pl-6">
                  <div className={`${styles.codeBlock} rounded-lg border ${theme === 'dark' ? 'border-white/10' : 'border-gray-700'} overflow-hidden`}>
                    <table className="w-full">
                      <thead className={`border-b ${theme === 'dark' ? 'border-white/10' : 'border-gray-700'}`}>
                        <tr>
                          <th className={`py-3 px-4 text-left ${styles.textSecondary} font-mono`}>EXTENSION</th>
                          <th className={`py-3 px-4 text-left ${styles.textSecondary} font-mono`}>ID</th>
                          <th className={`py-3 px-4 text-left ${styles.textSecondary} font-mono hidden md:table-cell`}>DESCRIÇÃO</th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${theme === 'dark' ? 'divide-white/10' : 'divide-gray-200'}`}>
                        <tr>
                          <td className={`py-3 px-4 ${styles.textMuted}`}>Spring Initializr</td>
                          <td className="py-3 px-4 font-mono text-sm text-[#f2055c]">vscjava.vscode-spring-initializr</td>
                          <td className={`py-3 px-4 ${styles.textMuted} hidden md:table-cell`}>Cria projetos Spring Boot via Spring Initializr</td>
                        </tr>
                        <tr>
                          <td className={`py-3 px-4 ${styles.textMuted}`}>Spring Boot Tools</td>
                          <td className="py-3 px-4 font-mono text-sm text-[#f2055c]">vmware.vscode-spring-boot</td>
                          <td className={`py-3 px-4 ${styles.textMuted} hidden md:table-cell`}>Suporte avançado ao Spring Boot</td>
                        </tr>
                        <tr>
                          <td className={`py-3 px-4 ${styles.textMuted}`}>Spring Boot Dashboard</td>
                          <td className="py-3 px-4 font-mono text-sm text-[#f2055c]">vscjava.vscode-spring-boot-dashboard</td>
                          <td className={`py-3 px-4 ${styles.textMuted} hidden md:table-cell`}>Gerencia aplicações Spring Boot</td>
                        </tr>
                        <tr>
                          <td className={`py-3 px-4 ${styles.textMuted}`}>Java Debug</td>
                          <td className="py-3 px-4 font-mono text-sm text-[#f2055c]">vscjava.vscode-java-debug</td>
                          <td className={`py-3 px-4 ${styles.textMuted} hidden md:table-cell`}>Depuração de aplicações Java</td>
                        </tr>
                        <tr>
                          <td className={`py-3 px-4 ${styles.textMuted}`}>Java Test Runner</td>
                          <td className="py-3 px-4 font-mono text-sm text-[#f2055c]">vscjava.vscode-java-test</td>
                          <td className={`py-3 px-4 ${styles.textMuted} hidden md:table-cell`}>Suporte a testes JUnit/TestNG</td>
                        </tr>
                        <tr>
                          <td className={`py-3 px-4 ${styles.textMuted}`}>Project Manager for Java</td>
                          <td className="py-3 px-4 font-mono text-sm text-[#f2055c]">vscjava.vscode-java-dependency</td>
                          <td className={`py-3 px-4 ${styles.textMuted} hidden md:table-cell`}>Visualização da hierarquia de classes</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Verification Section */}
          <div className={`${styles.card} border rounded-lg overflow-hidden`}>
            <div className="p-6 space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#f2055c]/10 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-[#f2055c]" />
                </div>
                <h3 className="text-2xl font-bold">Verificação</h3>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-2 text-lg font-semibold">
                  <span className="text-[#f2055c]">01.</span>
                  Teste de Instalação
                </div>
                <div className="pl-6 space-y-4">
                  <div>
                    <h4 className="text-[#f2055c] mb-2 font-medium">Criar arquivo HelloWorld.java</h4>
                    <div className={`${styles.codeBlock} rounded-lg p-4 font-mono text-sm border ${theme === 'dark' ? 'border-white/10' : 'border-gray-700'}`}>
                      <div className="flex justify-between items-center mb-2">
                        <span className={`text-xs ${styles.textSecondary}`}>Java</span>
                      </div>
                      <code className="text-gray-300 whitespace-pre">
{`public class HelloWorld {
    public static void main(String[] args) {
        System.out.println("Hello, Java 17! 🚀");
    }
}`}</code>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-2 text-lg font-semibold">
                  <span className="text-[#f2055c]">02.</span>
                  Comandos de Verificação
                </div>
                <div className="pl-6">
                  <div className={`${styles.codeBlock} rounded-lg p-4 font-mono text-sm border ${theme === 'dark' ? 'border-white/10' : 'border-gray-700'}`}>
                    <div className="flex justify-between items-center mb-2">
                      <span className={`text-xs ${styles.textSecondary}`}>Terminal</span>
                    </div>
                    <code className="text-gray-300 whitespace-pre">
{`# Verificar versão do Java
java --version

# Verificar compilador Java
javac --version

# Verificar variável JAVA_HOME
echo $JAVA_HOME  # Linux/macOS
echo %JAVA_HOME% # Windows`}</code>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-2 text-lg font-semibold">
                  <span className="text-[#f2055c]">03.</span>
                  Verificação da Instalação
                </div>
                <div className="pl-6">
                  <div className={`${styles.codeBlock} rounded-lg p-4 font-mono text-sm border ${theme === 'dark' ? 'border-white/10' : 'border-gray-700'}`}>
                    <div className="flex justify-between items-center mb-2">
                      <span className={`text-xs ${styles.textSecondary}`}>Terminal</span>
                    </div>
                    <code className="text-gray-300 whitespace-pre">
{`# Verificar versão do Java
java --version

# Verificar compilador Java
javac --version

# Verificar variável JAVA_HOME
echo $JAVA_HOME  # Linux/macOS
echo %JAVA_HOME% # Windows`}</code>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DocsPage; 